import { useEffect, useRef, useState } from "react";

import { CtaGroup } from "./components/CtaGroup.js";
import { BRAND, LANDING_PAGE } from "./content/brand.js";

/* --- S1 ---------------------------------------------------------------- */

function Hero() {
  const { hero } = LANDING_PAGE;

  return (
    <section className="section section--hero" aria-labelledby="hero-title">
      <p className="eyebrow">
        <span className="eyebrow-mark">{BRAND.name}</span>
        <span className="eyebrow-rule" aria-hidden="true" />
        {hero.eyebrowTagline}
      </p>
      <h1 id="hero-title">{hero.headline}</h1>
      <p className="lede">{hero.lede}</p>
      <p className="status-note">{hero.status}</p>
      <CtaGroup placement="hero" secondaryHref="#problem" />
    </section>
  );
}

/* --- S2 ---------------------------------------------------------------- */

function Problem() {
  const { problem } = LANDING_PAGE;

  return (
    <section className="section section--problem" id="problem" aria-labelledby="problem-title">
      <h2 id="problem-title">{problem.heading}</h2>
      <p className="body">{problem.body}</p>
    </section>
  );
}

/* --- S3 ---------------------------------------------------------------- */

function ScopeDiagram() {
  const { scope } = LANDING_PAGE.workspace;

  return (
    <div className="scope">
      <div className="scope-box scope-box--inside">
        <p className="scope-label">{scope.insideLabel}</p>
        <p className="scope-path">{scope.insidePath}</p>
        <p className="scope-contents">
          <span className="scope-branch" aria-hidden="true">
            {"└─ "}
          </span>
          {scope.insideContents.join(" · ")}
        </p>
      </div>
      <div className="scope-box scope-box--outside">
        <p className="scope-label">{scope.outsideLabel}</p>
        <p className="scope-excluded">
          {scope.outsideItems.map((item, index) => (
            <span key={item}>
              {index > 0 ? <span aria-hidden="true">{" · "}</span> : null}
              <s>{item}</s>
            </span>
          ))}
        </p>
      </div>
    </div>
  );
}

function Workspace() {
  const { workspace } = LANDING_PAGE;

  return (
    <section className="section" aria-labelledby="workspace-title">
      <h2 id="workspace-title">{workspace.heading}</h2>
      <p className="body">{workspace.body}</p>
      <ScopeDiagram />
      <p className="pull">{workspace.pull}</p>
    </section>
  );
}

/* --- S4 ---------------------------------------------------------------- */

function Learns() {
  const { learns } = LANDING_PAGE;

  return (
    <section className="section" aria-labelledby="learns-title">
      <h2 id="learns-title">{learns.heading}</h2>
      <p className="body">{learns.body}</p>
    </section>
  );
}

/* --- S5 — centrepiece --------------------------------------------------- */

/** The one animation on the page: the loop-back draws once on scroll-into-view. */
function useDrawOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDrawn(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setDrawn(true);
          observer.disconnect();
        }
      },
      { threshold: 0.6 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, drawn };
}

function WorkedExample() {
  const { workedExample } = LANDING_PAGE;
  const { diagram } = workedExample;
  const loop = useDrawOnce<HTMLParagraphElement>();

  return (
    <section className="section section--example" aria-labelledby="example-title">
      <p className="eyebrow">{workedExample.eyebrow}</p>
      <h2 id="example-title">{workedExample.heading}</h2>
      <p className="lede">{workedExample.sub}</p>

      <div
        className="flow"
        role="img"
        aria-label={`${BRAND.name} turns "update the deck" into a plan, routes each step to the model suited to it — deep models for judgment, a fast model for the known data shape — moves a step elsewhere when a provider is slow or down, checks whether the result was accepted, then remembers the recipe so next quarter runs cheaper.`}
      >
        <div className="flow-node flow-node--start">{diagram.input}</div>
        <span className="flow-arrow" aria-hidden="true">
          {"↓"}
        </span>
        <div className="flow-node flow-node--accent">{diagram.plan}</div>
        <span className="flow-arrow" aria-hidden="true">
          {"↓"}
        </span>

        <div className="flow-band">
          <p className="flow-band-label">{diagram.bandLabel}</p>
          <div className="flow-lanes">
            {diagram.lanes.map((lane) => (
              <div className="flow-lane" key={lane.task}>
                <div className="flow-node">{lane.task}</div>
                <span className="flow-arrow flow-arrow--lane" aria-hidden="true">
                  {"↓"}
                </span>
                <div className="flow-node flow-node--model">{lane.model}</div>
                <p className="flow-why">{lane.why}</p>
              </div>
            ))}
          </div>
          <p className="flow-failover">{diagram.failover}</p>
        </div>

        <span className="flow-arrow" aria-hidden="true">
          {"↓"}
        </span>
        <div className="flow-node">{diagram.check}</div>
        <span className="flow-arrow" aria-hidden="true">
          {"↓"}
        </span>
        <div className="flow-node flow-node--accent">{diagram.remember}</div>
        <p className={`flow-loop${loop.drawn ? " flow-loop--drawn" : ""}`} ref={loop.ref}>
          <span className="flow-loop-glyph" aria-hidden="true">
            {"↺"}
          </span>
          {diagram.loop}
        </p>
      </div>

      <p className="caption">{workedExample.caption}</p>
    </section>
  );
}

/* --- S6 ---------------------------------------------------------------- */

function Leverage() {
  const { leverage } = LANDING_PAGE;

  return (
    <section className="section" aria-labelledby="leverage-title">
      <h2 id="leverage-title">{leverage.heading}</h2>
      <p className="body">{leverage.body}</p>
    </section>
  );
}

/* --- S7 ---------------------------------------------------------------- */

function Cost() {
  const { cost } = LANDING_PAGE;

  return (
    <section className="section" aria-labelledby="cost-title">
      <h2 id="cost-title">{cost.heading}</h2>
      <p className="body">{cost.body}</p>
      <p className="kicker">{cost.kicker}</p>
    </section>
  );
}

/* --- S8 ---------------------------------------------------------------- */

function Objections() {
  const { objections } = LANDING_PAGE;

  return (
    <section className="section section--objections" aria-labelledby="objections-title">
      <h2 id="objections-title">{objections.heading}</h2>
      <div className="card-row card-row--3">
        {objections.items.map((item) => (
          <div className="card" key={item.question}>
            <h3>{item.question}</h3>
            <p>{item.answer}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* --- S9 ---------------------------------------------------------------- */

function Close() {
  const { close } = LANDING_PAGE;

  return (
    <section className="section section--close" aria-labelledby="close-title">
      <p className="quote" id="close-title">
        {close.quote}
      </p>
      <CtaGroup placement="close" />
    </section>
  );
}

/* --- footer ------------------------------------------------------------- */

function Footer() {
  return (
    <footer className="site-footer">
      <p>
        <strong>{BRAND.name}</strong> — {BRAND.footerLine}
      </p>
      <a href={`mailto:${BRAND.contactEmail}`}>{BRAND.contactEmail}</a>
    </footer>
  );
}

export function App() {
  // Title, description and data-theme are static in index.html — setting them
  // from JS would hide them from crawlers that do not run scripts.
  return (
    <div className="app-shell">
      <main>
        <Hero />
        <Problem />
        <Workspace />
        <Learns />
        <WorkedExample />
        <Leverage />
        <Cost />
        <Objections />
        <Close />
      </main>
      <Footer />
    </div>
  );
}
